// src/modules/upgrade-application/pages/UpgradeApplicationWizard.tsx
// Self-contained upgrade wizard. The upgrade application is created on step 1
// and filled through its OWN steps (separate from probationer applications):
//   steps 1-7 = details · acknowledgement · payment.
//   Fellow upgrades insert two steps of their own — 8 for the fellowship
//   record (papers/dissertation/benevolence) and 9 for the citation and
//   QSRBN practice licence — pushing acknowledgement to 10 and payment to 11.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpCircle, Lock, Info, Loader2, AlertTriangle, X } from "lucide-react";

import Step1Bio, { Step1BioInitial } from "../../application/components/steps/Step1Bio";
import Step2Qualifications, { QualificationRow } from "../../application/components/steps/Step2Qualifications";
import Step3OLevel, { Step3OLevelInitial } from "../../application/components/steps/Step3OLevel";
import Step4Memberships, { Step4MembershipRow } from "../../application/components/steps/Step4Memberships";
import Step5Experience, { Step5ExperienceRow } from "../../application/components/steps/Step5Experience";
import Step6Seminars, { Step6SeminarRow } from "../../application/components/steps/Step6Seminars";
import Step7Referees, { Step7RefereeInitial, SelectedMember } from "../../application/components/steps/Step7Referees";
import Step8Acknowledge from "../../application/components/steps/Step8Acknowledge";
import FellowshipStep, { FellowshipInitial, FellowshipPayload } from "../components/steps/FellowshipStep";
import FellowshipDocumentsStep from "../components/steps/FellowshipDocumentsStep";
import UpgradePaymentStep, { noUpgradeReason } from "../components/UpgradePaymentStep";

import { useGetActiveUserCategoriesQuery } from "../../application/api/probationerApplicationApi";
import {
  useGetUpgradeEligibilityQuery,
  useGetUpgradeCurrentQuery,
  useUpgradeStep1Mutation,
  useUpgradeStep2Mutation,
  useUpgradeStep3Mutation,
  useUpgradeStep4Mutation,
  useUpgradeStep5Mutation,
  useUpgradeStep6Mutation,
  useUpgradeStep7Mutation,
  useUpgradeFellowshipMutation,
  useUpgradeFellowshipDocumentsMutation,
  useUpgradeAcknowledgeMutation,
} from "../api/upgradeApi";

// Member upgrades: 1-7 details, 8 acknowledge, 9 payment.
// Fellow upgrades add two steps of their own — 8 for the fellowship record
// (papers/dissertation/benevolence) and 9 for the citation and QSRBN
// practice licence — pushing acknowledge to 10 and payment to 11.
// The server is what decides this: see ACK_STEP below.
const STORAGE_BASE = "https://api.niqsng.org/storage";

function toStorageUrl(path?: string | null): string {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${STORAGE_BASE}/${path.replace(/^\//, "")}`;
}

function safeMsg(err: any): string | null {
  if (err?.name === "AbortError" || err?.error === "AbortError" || err?.data?.error === "AbortError") return null;
  const d = err?.data ?? err;
  if (d?.errors && typeof d.errors === "object") {
    const k = Object.keys(d.errors)[0];
    const m = Array.isArray(d.errors[k]) ? d.errors[k][0] : String(d.errors[k]);
    return m || d?.message || "Validation failed";
  }
  return String(d?.message || d?.error || err?.error || "Something went wrong");
}

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export default function UpgradeApplicationWizard() {

  const { data: eligibility, isLoading: eligLoading } = useGetUpgradeEligibilityQuery();
  const { data: draft, isFetching: draftFetching, refetch: refetchDraft } = useGetUpgradeCurrentQuery();
  const { data: categories = [] } = useGetActiveUserCategoriesQuery();

  const upgradeToName: string | undefined = (eligibility as any)?.target_grade?.name ?? undefined;

  // Fellow upgrades carry an extra data step; the backend flags it on eligibility.
  const isFellow: boolean =
    Boolean((eligibility as any)?.is_fellow_upgrade) ||
    (upgradeToName ?? "").toLowerCase() === "fellow";
  const FELLOWSHIP_STEP = 8; // fellow only — papers / dissertation / benevolence
  const FELLOW_DOCS_STEP = 9; // fellow only — citation + QSRBN practice licence

  /* THE SERVER'S NUMBERING, not a second copy of it.
   *
   * `acknowledge_step` comes from the same helper the API uses to stamp
   * `completion_step` when a step is saved, so the two cannot disagree. Derived
   * here instead, they could: the API decides "is this a fellow upgrade?" from
   * the DRAFT's target category, this page from the member's eligibility, and
   * where those two answered differently the wizard expected a ten-step flow
   * while the server wrote a nine-step one. The sync effect below then clamped
   * the wizard back to Acknowledge every time — with payment one step past the
   * end of what the server would allow, and nothing on screen to explain it.
   *
   * The local guess stays as the fallback for an API that does not send it. */
  const ACK_STEP: number = Number((eligibility as any)?.acknowledge_step) || (isFellow ? 9 : 8);
  const PAY_STEP = ACK_STEP + 1;
  const TOTAL_STEPS = PAY_STEP;

  const [currentStep, setCurrentStep] = useState(1);
  const [applicationId, setApplicationId] = useState<string | number | undefined>(undefined);
  const [localError, setLocalError] = useState<string | null>(null);
  /* The step a refusal points at, when the server names one.
     A 422 that says "upload your documents first" knows exactly where that is
     done — carrying the number through turns the message into a way out
     instead of an instruction to go hunting. */
  const [errorStep, setErrorStep] = useState<number | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  const d: any = draft || null;

  // Sync to the server draft on load.
  const serverNextStep = useMemo(() => clamp(Number(d?.completion_step || 0) + 1, 1, TOTAL_STEPS), [d?.completion_step, TOTAL_STEPS]);
  useEffect(() => {
    if (d?.id) {
      setApplicationId(d.id);
      setCurrentStep((prev) => clamp(prev || serverNextStep, 1, serverNextStep));
    }
  }, [d?.id, d?.completion_step, serverNextStep]);

  // mutations
  const [step1, s1] = useUpgradeStep1Mutation();
  const [step2, s2] = useUpgradeStep2Mutation();
  const [step3, s3] = useUpgradeStep3Mutation();
  const [step4, s4] = useUpgradeStep4Mutation();
  const [step5, s5] = useUpgradeStep5Mutation();
  const [step6, s6] = useUpgradeStep6Mutation();
  const [step7, s7] = useUpgradeStep7Mutation();
  const [fellowship, sF] = useUpgradeFellowshipMutation();
  const [fellowshipDocuments, sFD] = useUpgradeFellowshipDocumentsMutation();
  const [ack, s8] = useUpgradeAcknowledgeMutation();

  const busy =
    draftFetching || s1.isLoading || s2.isLoading || s3.isLoading || s4.isLoading ||
    s5.isLoading || s6.isLoading || s7.isLoading || sF.isLoading || sFD.isLoading || s8.isLoading;

  const canGoToStep = (step: number) => step >= 1 && step <= serverNextStep;
  const goToStep = (step: number) => {
    if (!canGoToStep(step)) return;
    setLocalError(null);
    setErrorStep(null);
    setLocalSuccess(null);
    setCurrentStep(step);
  };

  /**
   * Show a refusal, and where it can be answered.
   *
   * The server sends `next_step` on the refusals that have a specific place
   * to go — the fellowship documents, for one. Everything else just shows the
   * message, as before.
   */
  const fail = (err: any) => {
    const m = safeMsg(err);
    if (m) setLocalError(m);

    const n = Number(err?.data?.next_step ?? err?.next_step);
    setErrorStep(Number.isFinite(n) && n > 0 ? n : null);
  };

  /** A refusal with somewhere to go: shown as a dialog, not a banner. */
  const blockingError = !!localError && !!errorStep && canGoToStep(errorStep);

  const dismissError = () => {
    setLocalError(null);
    setErrorStep(null);
  };

  /** What the button on a refusal should say. */
  const stepNameFor = (step: number): string => {
    if (step === PAY_STEP) return "Go to payment";
    if (step === ACK_STEP) return "Go to acknowledgement";
    if (isFellow && step === FELLOW_DOCS_STEP) return "Upload the documents";
    if (isFellow && step === FELLOWSHIP_STEP) return "Go to fellowship details";
    return `Go to step ${step}`;
  };
  const goBack = () => goToStep(currentStep - 1);
  const goNext = () => goToStep(currentStep + 1);

  const jumpNext = (msg: string) => {
    setLocalError(null);
    setLocalSuccess(msg);
    setCurrentStep((prev) => clamp(prev + 1, 1, TOTAL_STEPS));
    window.setTimeout(() => setLocalSuccess(null), 700);
  };

  const ensureId = () => {
    if (!applicationId) throw new Error("Please complete Step 1 first.");
    return applicationId;
  };

  // ── Initial values from the draft ──────────────────────────────────────
  const step1Initial: Step1BioInitial | null = useMemo(() => {
    if (!d) return null;
    return {
      surname: d.surname ?? "",
      other_names: d.other_names ?? "",
      title: d.title ?? "",
      gender: d.gender ?? "",
      postal_address: d.postal_address ?? "",
      residential_address: d.residential_address ?? "",
      email: d.email ?? "",
      phone: d.phone ?? "",
      date_of_birth: d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : "",
      nationality: d.nationality ?? "",
      application_category_id: d.application_category_id ?? null,
      state_id: d.state_id ?? null,
      local_government_id: d.lga_id ?? null,
      sector_id: d.sector_id ?? null,
      chapter_id: d.chapter_id ?? null,
      chapter_other: d.chapter_other ?? "",
      pro_pic_url: toStorageUrl(d.pro_pic),
      signature_url: toStorageUrl(d.signature),
    };
  }, [d]);

  const step2Initial = useMemo(() => {
    if (!d) return null;
    const qs = Array.isArray(d.qualifications) ? d.qualifications : [];
    return { qualifications: qs.map((q: any) => ({ institution: q?.institution ?? "", qualification: q?.qualification ?? "", year: String(q?.year ?? ""), cert_url: toStorageUrl(q?.cert_file), cert_file: q?.cert_file ?? undefined })) } as { qualifications: QualificationRow[] };
  }, [d]);

  const step3Initial: Step3OLevelInitial | null = useMemo(() => {
    if (!d) return null;
    const raw = Array.isArray(d.olevel_results) ? d.olevel_results : [];
    const prelimRaw = Array.isArray(d.prelim_results) ? d.prelim_results : [];
    return {
      results: raw.map((r: any) => ({ exam_type: (r?.exam_type ?? "waec") as any, exam_year: String(r?.exam_year ?? ""), exam_number: r?.exam_number ?? "", subjects: r?.subjects ?? {}, result_file: r?.result_file ?? undefined })),
      prelim_results: prelimRaw.map((p: any) => ({
        exam_type: (p?.exam_type ?? "prelim") as any,
        other_exam_name: p?.other_exam_name ?? "",
        exam_year: String(p?.exam_year ?? ""),
        institution: p?.institution ?? "",
        exam_number: p?.exam_number ?? "",
        subjects: p?.subjects ?? {},
        result_file: p?.result_file ?? undefined,
      })),
    };
  }, [d]);

  const step4Initial = useMemo(() => {
    if (!d) return null;
    const ms = Array.isArray(d.professional_memberships) ? d.professional_memberships : [];
    return { memberships: ms.map((m: any) => ({ body_name: m?.body_name ?? "", stage_passed: m?.stage_passed ?? "", certificate_path: m?.certificate_path ?? "" })) } as { memberships: Step4MembershipRow[] };
  }, [d]);

  const step5Initial = useMemo(() => {
    if (!d) return null;
    const xs = Array.isArray(d.experiences) ? d.experiences : [];
    return { experiences: xs.map((x: any) => ({ organization: x?.organization ?? "", position: x?.position ?? "", start_date: x?.start_date ? String(x.start_date).slice(0, 10) : "", end_date: x?.end_date ? String(x.end_date).slice(0, 10) : "", responsibilities: x?.responsibilities ?? "" })) } as { experiences: Step5ExperienceRow[] };
  }, [d]);

  const step6Initial = useMemo(() => {
    if (!d) return null;
    const ss = Array.isArray(d.seminars) ? d.seminars : [];
    return { seminars: ss.map((s: any) => ({ title: s?.title ?? "", date: s?.date ? String(s.date).slice(0, 10) : "", location: s?.location ?? "" })) } as { seminars: Step6SeminarRow[] };
  }, [d]);

  const step7Initial: Step7RefereeInitial | null = useMemo(() => {
    if (!d) return null;
    const rr = Array.isArray(d.referees) ? d.referees : [];
    const toMember = (r: any): SelectedMember | undefined => {
      const m = r?.member;
      if (m?.id) return { id: m.id, member_id: m?.member_id ?? String(m.id), surname: m?.surname, other_names: m?.othernames ?? m?.other_names, email: m?.email, membership_no: m?.membership_no };
      if (r?.member_id) return { id: r.member_id, member_id: String(r.member_id) };
      return undefined;
    };
    const a = rr[0] || {}, b = rr[1] || {};
    return { referees: [{ member: toMember(a), relationship: a?.relationship ?? "" }, { member: toMember(b), relationship: b?.relationship ?? "" }] };
  }, [d]);

  // Fellow-only step: papers (14), dissertation (15), benevolence (16).
  const fellowshipInitial: FellowshipInitial | null = useMemo(() => {
    if (!d) return null;
    const papers = Array.isArray(d.papers) ? d.papers : [];
    const bens = Array.isArray(d.benevolences) ? d.benevolences : [];
    return {
      papers: papers.map((p: any) => ({ title: p?.title ?? "", presented_at: p?.presented_at ?? "", year: String(p?.year ?? "") })),
      benevolences: bens.map((b: any) => ({ description: b?.description ?? "", type: b?.type ?? "", year: String(b?.year ?? "") })),
      dissertation_topic: d.dissertation_topic ?? "",
      dissertation_file: d.dissertation_file ?? "",
      dissertation_url: toStorageUrl(d.dissertation_file),
    };
  }, [d]);

  // Step 8 overview reuses the probationer Step8Acknowledge, which reads
  // probationer-shaped keys. Map the upgrade relation names onto those keys.
  const overviewData = useMemo(() => {
    if (!d) return d;
    return {
      ...d,
      o_level_results: d.olevel_results ?? [],
      work_experiences: d.experiences ?? [],
      // qualifications, professional_memberships, seminars, referees already match
    };
  }, [d]);

  // ── Submit handlers ────────────────────────────────────────────────────
  const onStep1 = async (p: any) => {
    setLocalError(null);
    try {
      const fd = new FormData();
      fd.append("surname", p.surname);
      fd.append("other_names", p.other_names);
      fd.append("title", p.title);
      fd.append("gender", p.gender);
      if (p.postal_address) fd.append("postal_address", p.postal_address);
      fd.append("residential_address", p.residential_address);
      fd.append("date_of_birth", p.date_of_birth);
      fd.append("nationality", p.nationality);
      fd.append("state_id", String(p.state_id));
      fd.append("local_government_id", String(p.local_government_id));
      if (p.sector_id) fd.append("sector_id", String(p.sector_id));
      if (p.chapter_id != null) fd.append("chapter_id", String(p.chapter_id));
      if (p.chapter_other) fd.append("chapter_other", p.chapter_other);
      if (p.pro_pic) fd.append("pro_pic", p.pro_pic);
      if (p.signature) fd.append("signature", p.signature);

      const res: any = await step1(fd).unwrap();
      if (res?.data?.id) setApplicationId(res.data.id);
      await refetchDraft();
      jumpNext(res?.message || "Step 1 saved.");
    } catch (err: any) {
      fail(err);
    }
  };

  const onStep2 = async (p: any) => {
    setLocalError(null);
    try {
      const id = ensureId();
      const fd = new FormData();
      p.qualifications.forEach((q: any, i: number) => {
        fd.append(`qualifications[${i}][institution]`, q.institution);
        fd.append(`qualifications[${i}][qualification]`, q.qualification);
        fd.append(`qualifications[${i}][year]`, q.year);
        // Send the existing stored path so the backend preserves it when no new
        // file is attached (otherwise re-saving would wipe the certificate).
        if (q.cert_file) fd.append(`qualifications[${i}][cert_file]`, q.cert_file);
        const f = p.certFilesByIndex?.[i];
        if (f) fd.append(`cert_files[${i}]`, f);
      });
      const res: any = await step2({ id, body: fd }).unwrap();
      await refetchDraft();
      jumpNext(res?.message || "Step 2 saved.");
    } catch (err: any) { fail(err); }
  };

  const onStep3 = async (
    results: any[],
    resultFilesByIndex: Record<number, File | null> = {},
    prelimResults: any[] = [],
    prelimFilesByIndex: Record<number, File | null> = {},
  ) => {
    setLocalError(null);
    try {
      const id = ensureId();
      const fd = new FormData();
      fd.append("results", JSON.stringify(results));
      Object.entries(resultFilesByIndex).forEach(([i, f]) => {
        if (f) fd.append(`result_files[${i}]`, f);
      });
      // Optional Prelim / IJMB records (upgrade applications only).
      fd.append("prelim_results", JSON.stringify(prelimResults));
      Object.entries(prelimFilesByIndex).forEach(([i, f]) => {
        if (f) fd.append(`prelim_files[${i}]`, f);
      });
      const res: any = await step3({ id, body: fd }).unwrap();
      await refetchDraft();
      jumpNext(res?.message || "Step 3 saved.");
    } catch (err: any) { fail(err); }
  };
  const onStep4 = async (memberships: any[]) => {
    setLocalError(null);
    try { const id = ensureId(); const res: any = await step4({ id, memberships }).unwrap(); await refetchDraft(); jumpNext(res?.message || "Step 4 saved."); }
    catch (err: any) { fail(err); }
  };
  const onStep5 = async (experiences: any[]) => {
    setLocalError(null);
    try { const id = ensureId(); const res: any = await step5({ id, experiences }).unwrap(); await refetchDraft(); jumpNext(res?.message || "Step 5 saved."); }
    catch (err: any) { fail(err); }
  };
  const onStep6 = async (seminars: any[]) => {
    setLocalError(null);
    try { const id = ensureId(); const res: any = await step6({ id, seminars }).unwrap(); await refetchDraft(); jumpNext(res?.message || "Step 6 saved."); }
    catch (err: any) { fail(err); }
  };
  const onStep7 = async (referees: any[]) => {
    setLocalError(null);
    try { const id = ensureId(); const res: any = await step7({ id, referees }).unwrap(); await refetchDraft(); jumpNext(res?.message || "Step 7 saved."); }
    catch (err: any) { fail(err); }
  };
  const onFellowship = async (p: FellowshipPayload) => {
    setLocalError(null);
    try {
      const id = ensureId();
      const fd = new FormData();
      p.papers.forEach((row, i) => {
        fd.append(`papers[${i}][title]`, row.title);
        if (row.presented_at) fd.append(`papers[${i}][presented_at]`, row.presented_at);
        if (row.year) fd.append(`papers[${i}][year]`, row.year);
      });
      p.benevolences.forEach((row, i) => {
        fd.append(`benevolences[${i}][description]`, row.description);
        if (row.type) fd.append(`benevolences[${i}][type]`, row.type);
        if (row.year) fd.append(`benevolences[${i}][year]`, row.year);
      });
      if (p.dissertation_topic) fd.append("dissertation_topic", p.dissertation_topic);
      if (p.dissertationFile) fd.append("dissertation_file", p.dissertationFile);
      else if (p.dissertation_file_path) fd.append("dissertation_file_path", p.dissertation_file_path);

      const res: any = await fellowship({ id, body: fd }).unwrap();
      await refetchDraft();
      jumpNext(res?.message || "Fellowship details saved.");
    } catch (err: any) { fail(err); }
  };
  const onFellowshipDocuments = async (fd: FormData) => {
    setLocalError(null);
    try {
      const id = ensureId();
      const res: any = await fellowshipDocuments({ id, body: fd }).unwrap();
      await refetchDraft();
      jumpNext(res?.message || "Documents saved.");
    } catch (err: any) { fail(err); }
  };
  const onStep8 = async () => {
    setLocalError(null);
    try { const id = ensureId(); const res: any = await ack({ id }).unwrap(); await refetchDraft(); jumpNext(res?.message || "Acknowledged."); }
    catch (err: any) { fail(err); }
  };

  // ── Eligibility guard ──────────────────────────────────────────────────
  if (eligLoading) {
    return (
      <div className="max-w-4xl mx-auto p-8 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" /> <p className="text-sm">Checking your eligibility…</p>
      </div>
    );
  }
  const elig: any = eligibility;
  if (elig && !elig.target_grade) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 grid place-items-center mx-auto mb-4"><Lock className="w-8 h-8 text-amber-600" /></div>
          <h1 className="text-xl font-bold text-amber-900">No upgrade available</h1>
          <p className="text-sm text-amber-800 mt-2 max-w-md mx-auto">{noUpgradeReason(elig)}</p>
          <Link to="/upgrade-applications" className="inline-block mt-6 px-4 py-2 rounded-lg border bg-white text-amber-700 text-sm font-medium hover:bg-amber-100">Back to my applications</Link>
        </div>
      </div>
    );
  }
  if (elig?.active_upgrade) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-100 grid place-items-center mx-auto mb-4"><Info className="w-8 h-8 text-indigo-600" /></div>
          <h1 className="text-xl font-bold text-indigo-900">Upgrade already in progress</h1>
          <p className="text-sm text-indigo-800 mt-2 max-w-md mx-auto">You already have an upgrade application being processed.</p>
          <Link to="/upgrade-applications" className="inline-block mt-6 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">View my applications</Link>
        </div>
      </div>
    );
  }

  const stepLabel =
    isFellow && currentStep === FELLOWSHIP_STEP ? "Fellowship"
    : isFellow && currentStep === FELLOW_DOCS_STEP ? "Documents"
    : currentStep === ACK_STEP ? "Acknowledge"
    : currentStep === PAY_STEP ? "Payment"
    : `Step ${currentStep}`;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      {/* Header */}
      <div className="mb-6 p-6 rounded-[32px] bg-gray-900 text-white shadow-xl space-y-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-[0.04]"><ArrowUpCircle className="w-40 h-40" /></div>
        <div className="flex items-start justify-between gap-4 relative z-10">
          <div>
            <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Grade Upgrade Application</div>
            <h1 className="text-2xl font-black text-white leading-tight">
              {elig?.current_grade?.name ?? "Upgrade"} → {elig?.target_grade?.name ?? ""}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-gray-400">{stepLabel} <span className="text-white/40">· {currentStep} of {TOTAL_STEPS}</span></p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[10px] font-bold uppercase tracking-wider">Details → Acknowledge → Pay</span>
            </div>
          </div>
          <Link to="/upgrade-applications"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/15 text-xs font-bold text-white/60 hover:text-white hover:border-white/30 transition-all">
            <ArrowLeft className="w-3 h-3" /> Back to applications
          </Link>
        </div>

        {/* Step pills */}
        <div className="flex items-center gap-2 flex-wrap relative z-10">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
            const step = i + 1;
            const allowed = canGoToStep(step);
            const done = step < currentStep;
            return (
              <button key={step} type="button" onClick={() => goToStep(step)} disabled={!allowed}
                title={allowed ? `Go to step ${step}` : "Complete previous steps first"}
                className={["h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all",
                  step === currentStep ? "bg-white text-gray-900 shadow-lg" : done ? "bg-white/20 text-white" : allowed ? "bg-white/10 text-white/50 hover:bg-white/20" : "bg-white/5 text-white/20 cursor-not-allowed"].join(" ")}>
                {step}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-white/10 relative z-10">
          <button type="button" onClick={goBack} disabled={currentStep <= 1 || busy} className="flex items-center gap-2 text-sm font-bold text-white/50 hover:text-white disabled:opacity-25"><ArrowLeft className="w-4 h-4" /> Back</button>
          <button type="button" onClick={goNext} disabled={currentStep >= serverNextStep || busy} className="flex items-center gap-2 text-sm font-bold text-white/50 hover:text-white disabled:opacity-25">Next <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>

      {localSuccess && <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">{localSuccess}</div>}
      {/* A refusal that names a step is a STOP, not a note. It is shown as a
          dialog over the page, because the banner version sat above a form the
          applicant was still looking at, and the thing they had to do next was
          on a different step entirely. Plain errors — a field that failed
          validation on the step in front of them — stay inline, where what
          they refer to is already on screen. */}
      {localError && !blockingError && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {localError}
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 md:p-6">
        {currentStep === 1 && <Step1Bio onSubmit={onStep1} busy={busy} initialValues={step1Initial} categories={categories} readOnly={false} upgradeToCategoryName={upgradeToName} />}
        {currentStep === 2 && <Step2Qualifications onSubmit={onStep2} busy={busy} onBack={goBack} initialValues={step2Initial} readOnly={false} />}
        {currentStep === 3 && <Step3OLevel onSubmit={onStep3} busy={busy} onBack={goBack} initialValues={step3Initial} readOnly={false} showPrelim />}
        {currentStep === 4 && <Step4Memberships onSubmit={onStep4} busy={busy} onBack={goBack} initialValues={step4Initial} readOnly={false} />}
        {currentStep === 5 && <Step5Experience onSubmit={onStep5} busy={busy} onBack={goBack} initialValues={step5Initial} readOnly={false} />}
        {currentStep === 6 && <Step6Seminars onSubmit={onStep6} busy={busy} onBack={goBack} initialValues={step6Initial} readOnly={false} />}
        {currentStep === 7 && <Step7Referees onSubmit={onStep7} busy={busy} onBack={goBack} initialValues={step7Initial} readOnly={false} />}
        {isFellow && currentStep === FELLOWSHIP_STEP && <FellowshipStep onSubmit={onFellowship} busy={busy} onBack={goBack} initialValues={fellowshipInitial} readOnly={false} />}
        {isFellow && currentStep === FELLOW_DOCS_STEP && (
          <FellowshipDocumentsStep
            onSubmit={onFellowshipDocuments}
            busy={busy}
            onBack={goBack}
            initialValues={{
              citation_file: d?.citation_file ?? null,
              qsrbn_licence_file: d?.qsrbn_licence_file ?? null,
            }}
            storageBase={toStorageUrl}
            readOnly={false}
          />
        )}
        {/* Acknowledging is not submitting here — the fee is. Said on the
            button, because an applicant who reads "Submit" stops, waits for a
            review nobody has been asked for, and reports having applied. */}
        {currentStep === ACK_STEP && (
          <Step8Acknowledge
            application={overviewData}
            onSubmit={onStep8}
            busy={busy}
            onBack={goBack}
            submitLabel="Confirm & Continue to Payment"
            busyLabel="Confirming…"
          />
        )}
        {currentStep === PAY_STEP && <UpgradePaymentStep onBack={goBack} />}
      </div>

      {blockingError && errorStep ? (
        <ActionRequiredDialog
          message={localError!}
          actionLabel={stepNameFor(errorStep)}
          onAction={() => { dismissError(); goToStep(errorStep); }}
          onClose={dismissError}
        />
      ) : null}
    </div>
  );
}

/**
 * A refusal the applicant has to act on, over the whole page.
 *
 * Two ways out and no third: go and do the thing, or close it. The point of
 * covering the page is that the previous version — a red line above a form —
 * was read as a note about the form, when what it actually meant was "the
 * next thing you must do is on another step".
 *
 * Closing is allowed. Trapping somebody in a dialog does not make them any
 * likelier to act, and the refusal comes straight back the next time they try
 * to move on.
 */
function ActionRequiredDialog({
  message,
  actionLabel,
  onAction,
  onClose,
}: {
  message: string;
  actionLabel: string;
  onAction: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-6 pt-6">
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900">One more thing first</h3>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-auto text-gray-300 hover:text-gray-600 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 mt-5 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-100"
          >
            Close
          </button>
          <button
            onClick={onAction}
            autoFocus
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
